// @ts-nocheck
import globalConfig from '../config/globalConfig';
import { imageSize, imageRoute } from '../constants/files';
import { getMimeTypeFromBuffer } from './mimeTypeHelper';

const isDevelop = globalConfig?.isDevelop ?? false;
const localBucket = globalConfig?.localBucket ?? '';

// Some service-copy responses contain a bucket key rather than a filename.
// Preserve its folder instead of prepending the current service's folder.
const getStoredBucketKey = (value) =>
  /^\/?tenant\/\d+\//.test(value ?? '') ? value.replace(/^\//, '') : null;

//Replace params to build files path
export const replaceParams = (params = {}) => {
  const { pathReference, tenantId, tripId } = params;

  return (
    imageRoute?.[pathReference]?.route
      ?.replaceAll('{tenantid}', tenantId)
      ?.replaceAll('{tripid}', tripId) ?? null
  );
};

//Generate image url from bucket based on image size prefixes(BL, TH, SM, MD, LD, etc).
export const getBucketImageUrl = (params = {}) => {
  const { fileName, sizePrefix } = params;

  // Return the fileName as-is if it's an absolute URL
  if (
    fileName &&
    (fileName.toLowerCase().startsWith('http://') ||
      fileName.toLowerCase().startsWith('https://'))
  ) {
    return fileName;
  }

  const route = replaceParams(params);

  const storedKey = getStoredBucketKey(fileName);
  if (storedKey) {
    const lastSlash = storedKey.lastIndexOf('/');
    const name = storedKey
      .substring(lastSlash + 1)
      .replace(/^(BL|TH|SM|MD|LG)-/, '');
    return `${isDevelop ? localBucket : ''}/${storedKey.substring(0, lastSlash)}/${sizePrefix || imageSize.medium.prefix}-${name}`;
  }

  return `${isDevelop ? localBucket : ''}/${route}/${sizePrefix || imageSize.medium.prefix}-${fileName}`;
};

//Generate files url from bucket.
export const getBucketFileUrl = (params = {}) => {
  const { fileName } = params;

  // Return the fileName as-is if it's an absolute URL
  if (
    fileName &&
    (fileName.toLowerCase().startsWith('http://') ||
      fileName.toLowerCase().startsWith('https://'))
  ) {
    return fileName;
  }

  const route = replaceParams(params);

  const storedKey = getStoredBucketKey(fileName);
  if (storedKey) return `${isDevelop ? localBucket : ''}/${storedKey}`;

  return `${isDevelop ? localBucket : ''}/${route}/${fileName}`;
};

//Generate a blob image from url
export const getFileFromUrl = async (url, filename, mimeType) => {
  try {
    const abortController = new AbortController();
    // Add cache-busting parameter to bypass Service Worker
    const cacheBustUrl = url.includes('?')
      ? `${url}&_cb=${Date.now()}`
      : `${url}?_cb=${Date.now()}`;

    let response = await fetch(cacheBustUrl, {
      mode: 'cors',
      cache: 'reload', // Force bypass Service Worker cache
      signal: abortController.signal
    });

    if (!response.ok) {
      // Fallback image
      response = await fetch('/img/TH-placeholder.png');
    }

    if (!response.ok) return null;

    const arrayBuffer = await response.arrayBuffer();
    const detectedMime = getMimeTypeFromBuffer(arrayBuffer);
    const responseContentType = response.headers.get('content-type');

    const contentType =
      responseContentType === 'multipart/form-data'
        ? detectedMime || mimeType
        : responseContentType || mimeType;

    return new File([arrayBuffer], filename, { type: contentType });
  } catch (error) {
    console.log('ERROR GETTING FILE:');
    console.log(error);
    return null;
  }
};

//Get best image size prefix
export const getBestSizePrefix = (width = 0) => {
  if (width < imageSize.small.size) {
    return 'TH';
  } else if (width < imageSize.medium.size) {
    return 'SM';
  } else if (width < imageSize.large.size) {
    return 'MD';
  } else {
    return 'MD'; // Default
  }
};

/**
 * Generate image URL for refactored LineItem images.
 * For use when enableRefactoredServices feature flag is ON.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.resourcePath - Full bucket-relative path (e.g., "tenant/1000/trip/1000/lineitems/6053/images/abc.jpg")
 * @param {string} [params.sizePrefix] - Optional size prefix (BL, TH, SM, MD, LG). If omitted, returns base/original image.
 * @returns {string} Full URL to the image
 *
 * @example
 * // Base/original image
 * getLineItemImageUrl({ resourcePath: "tenant/1000/trip/1000/lineitems/6053/images/photo.jpg" })
 * // Returns: "https://bucket/tenant/1000/trip/1000/lineitems/6053/images/photo.jpg"
 *
 * // Thumbnail variant
 * getLineItemImageUrl({ resourcePath: "tenant/1000/trip/1000/lineitems/6053/images/photo.jpg", sizePrefix: "TH" })
 * // Returns: "https://bucket/tenant/1000/trip/1000/lineitems/6053/images/TH-photo.jpg"
 */
export const getLineItemImageUrl = (params = {}) => {
  const { resourcePath, sizePrefix } = params;

  if (!resourcePath) {
    return null;
  }

  // Return as-is if it's already an absolute URL
  if (
    resourcePath.toLowerCase().startsWith('http://') ||
    resourcePath.toLowerCase().startsWith('https://')
  ) {
    return resourcePath;
  }

  // Split path into directory and filename
  const lastSlashIndex = resourcePath.lastIndexOf('/');
  const directory = resourcePath.substring(0, lastSlashIndex);
  const fileName = resourcePath.substring(lastSlashIndex + 1);

  // Build the final path with optional size prefix
  const finalPath = sizePrefix
    ? `${directory}/${sizePrefix}-${fileName}`
    : resourcePath;

  // Return full URL with bucket base
  return `${isDevelop ? localBucket : ''}/${finalPath}`;
};

/**
 * Generate attachment URL for refactored LineItem attachments.
 * For use when enableRefactoredServices feature flag is ON.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.resourcePath - Full bucket-relative path (e.g., "tenant/1000/trip/1000/lineitems/6053/attachments/doc.pdf")
 * @returns {string} Full URL to the attachment
 */
export const getLineItemAttachmentUrl = (params = {}) => {
  const { resourcePath } = params;

  if (!resourcePath) {
    return null;
  }

  // Return as-is if it's already an absolute URL
  if (
    resourcePath.toLowerCase().startsWith('http://') ||
    resourcePath.toLowerCase().startsWith('https://')
  ) {
    return resourcePath;
  }

  // Return full URL with bucket base
  return `${isDevelop ? localBucket : ''}/${resourcePath}`;
};

//Check if image is available
export const imageExistsInBucket = async (url, retries = 10, delay = 2000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      if (response.ok) return true;
    } catch (error) {
      console.error('Error on Image reintent ', error);
    }
    await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
  }
  return false;
};
